import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  usePlatformSettings,
  useUpdatePlatformSettings,
  type PlatformSettings,
  type UpdatePlatformSettings,
} from '@/api/hooks/usePlatformSettings';
import type { AppError } from '@/api/client';

/**
 * Last platform admin sidebar section. Four stacked cards — default
 * platform fee, contact, feature flags, and a read-only platform
 * summary. Each editable card has its own Save button so saving one
 * section doesn't clobber unsaved edits in another.
 */
export default function PlatformSettingsPage() {
  const { data, isLoading, isError } = usePlatformSettings();
  const update = useUpdatePlatformSettings();

  if (isLoading) {
    return (
      <Shell>
        <p className="text-sm text-neutral-400">Loading settings…</p>
      </Shell>
    );
  }
  if (isError || !data) {
    return (
      <Shell>
        <p className="text-sm text-red-600">Failed to load settings.</p>
      </Shell>
    );
  }

  const save = (patch: UpdatePlatformSettings, label: string) => {
    update.mutate(patch, {
      onSuccess: () => toast.success(`${label} saved`),
      onError: (err) =>
        toast.error(
          (err as unknown as AppError)?.message ?? `Failed to save ${label.toLowerCase()}`,
        ),
    });
  };

  return (
    <Shell>
      <FeeCard settings={data} onSave={save} saving={update.isPending} />
      <ContactCard settings={data} onSave={save} saving={update.isPending} />
      <FlagsCard settings={data} onSave={save} saving={update.isPending} />
      <SummaryCard settings={data} />
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-6 py-8 md:px-10 md:py-10">
      <header>
        <p className="text-xs font-mono uppercase tracking-[0.18em] text-neutral-400">
          Platform Admin
        </p>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight text-neutral-900 md:text-3xl">
          Settings
        </h1>
        <p className="mt-1 text-sm text-neutral-500">
          Platform-wide defaults and feature flags.
        </p>
      </header>
      <div className="mt-6 flex max-w-3xl flex-col gap-4">{children}</div>
    </div>
  );
}

type SaveFn = (patch: UpdatePlatformSettings, label: string) => void;

function FeeCard({
  settings,
  onSave,
  saving,
}: {
  settings: PlatformSettings;
  onSave: SaveFn;
  saving: boolean;
}) {
  const [value, setValue] = useState(String(settings.defaultPlatformFeePercent));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setValue(String(settings.defaultPlatformFeePercent));
  }, [settings.defaultPlatformFeePercent]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed < 0 || parsed > 25) {
      setError('Fee must be between 0 and 25%');
      return;
    }
    setError(null);
    onSave(
      {
        defaultPlatformFeePercent: parsed,
        publicSignupEnabled: settings.publicSignupEnabled,
        marketplaceEnabled: settings.marketplaceEnabled,
        demoRequestsEnabled: settings.demoRequestsEnabled,
      },
      'Default fee',
    );
  };

  return (
    <Card title="Platform fee">
      <form onSubmit={submit} className="flex flex-col gap-3">
        <label className="flex items-end gap-3 text-sm">
          <span className="flex flex-col">
            <span className="text-xs font-medium text-neutral-500">Default fee</span>
            <span className="mt-1 inline-flex items-center overflow-hidden rounded-md border border-neutral-200 bg-white focus-within:border-[#C2713A] focus-within:ring-2 focus-within:ring-[#C2713A]/30">
              <input
                type="number"
                step="0.01"
                min="0"
                max="25"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                className="w-24 bg-transparent px-3 py-2 text-right font-mono text-sm text-neutral-900 focus:outline-none"
              />
              <span className="px-3 py-2 text-xs text-neutral-500">%</span>
            </span>
          </span>
          <button
            type="submit"
            disabled={saving}
            className="rounded-md bg-[#C2713A] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#a95f2f] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </label>
        {error && <p className="text-xs text-red-600">{error}</p>}
        <p className="text-xs text-neutral-500">
          Applied to newly onboarded operators. Existing operators keep their current rate
          unless you override it from the tenant detail page.
        </p>
      </form>
    </Card>
  );
}

function ContactCard({
  settings,
  onSave,
  saving,
}: {
  settings: PlatformSettings;
  onSave: SaveFn;
  saving: boolean;
}) {
  const [support, setSupport] = useState(settings.supportEmail);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setSupport(settings.supportEmail);
  }, [settings.supportEmail]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!support.trim() || !support.includes('@')) {
      setError('Enter a valid email');
      return;
    }
    setError(null);
    onSave(
      {
        supportEmail: support.trim(),
        publicSignupEnabled: settings.publicSignupEnabled,
        marketplaceEnabled: settings.marketplaceEnabled,
        demoRequestsEnabled: settings.demoRequestsEnabled,
      },
      'Support email',
    );
  };

  return (
    <Card title="Contact">
      <form onSubmit={submit} className="flex flex-col gap-3">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <ReadOnlyField label="Platform admin email" value={settings.platformAdminEmail} hint="env" />
          <ReadOnlyField label="Frontend URL" value={settings.frontendUrl} hint="env" />
        </div>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-xs font-medium text-neutral-500">Support email</span>
          <div className="flex gap-3">
            <input
              type="email"
              value={support}
              onChange={(e) => setSupport(e.target.value)}
              className="w-72 rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-900 focus-visible:border-[#C2713A] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C2713A]/30"
            />
            <button
              type="submit"
              disabled={saving}
              className="rounded-md bg-[#C2713A] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#a95f2f] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
          {error && <span className="text-xs text-red-600">{error}</span>}
        </label>
      </form>
    </Card>
  );
}

function FlagsCard({
  settings,
  onSave,
  saving,
}: {
  settings: PlatformSettings;
  onSave: SaveFn;
  saving: boolean;
}) {
  const [flags, setFlags] = useState({
    publicSignupEnabled: settings.publicSignupEnabled,
    marketplaceEnabled: settings.marketplaceEnabled,
    demoRequestsEnabled: settings.demoRequestsEnabled,
  });

  useEffect(() => {
    setFlags({
      publicSignupEnabled: settings.publicSignupEnabled,
      marketplaceEnabled: settings.marketplaceEnabled,
      demoRequestsEnabled: settings.demoRequestsEnabled,
    });
  }, [settings.publicSignupEnabled, settings.marketplaceEnabled, settings.demoRequestsEnabled]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(flags, 'Feature flags');
  };

  return (
    <Card title="Feature flags">
      <form onSubmit={submit} className="flex flex-col gap-3">
        <Flag
          label="Public operator signup"
          hint="Phase 2 — operators can self-register without platform admin"
          checked={flags.publicSignupEnabled}
          onChange={(v) => setFlags({ ...flags, publicSignupEnabled: v })}
        />
        <Flag
          label="Marketplace"
          hint="Show /venues public marketplace"
          checked={flags.marketplaceEnabled}
          onChange={(v) => setFlags({ ...flags, marketplaceEnabled: v })}
        />
        <Flag
          label="Demo requests"
          hint="Accept submissions on the /demo form"
          checked={flags.demoRequestsEnabled}
          onChange={(v) => setFlags({ ...flags, demoRequestsEnabled: v })}
        />
        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={saving}
            className="rounded-md bg-[#C2713A] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#a95f2f] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
          <p className="text-xs text-neutral-500">
            Flags are informational for MVP — server-side enforcement ships in Phase 2.
          </p>
        </div>
      </form>
    </Card>
  );
}

function SummaryCard({ settings }: { settings: PlatformSettings }) {
  return (
    <Card title="Platform summary">
      <dl className="grid grid-cols-3 gap-3 text-sm">
        <Stat label="Total operators" value={settings.totalTenants} />
        <Stat label="Total locations" value={settings.totalLocations} />
        <Stat label="Total units" value={settings.totalUnits} />
      </dl>
    </Card>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-neutral-200 bg-white p-5 shadow-sm">
      <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-neutral-500">
        {title}
      </h2>
      {children}
    </section>
  );
}

function ReadOnlyField({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className="text-sm">
      <p className="text-xs font-medium text-neutral-500">{label}</p>
      <p className="mt-1 flex items-center gap-2">
        <span className="font-mono text-neutral-700">{value}</span>
        {hint && (
          <span className="rounded bg-neutral-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-neutral-500">
            {hint}
          </span>
        )}
      </p>
    </div>
  );
}

function Flag({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3 rounded-md border border-neutral-100 bg-neutral-50 p-3 hover:border-neutral-200">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 rounded border-neutral-300 text-[#C2713A] focus:ring-[#C2713A]/30"
      />
      <div className="flex-1">
        <p className="text-sm font-medium text-neutral-900">{label}</p>
        <p className="text-xs text-neutral-500">{hint}</p>
      </div>
    </label>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wider text-neutral-500">{label}</dt>
      <dd className="mt-1 font-mono text-xl font-semibold text-neutral-900">{value}</dd>
    </div>
  );
}
